import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@actual-app/components/button';
import { css, keyframes } from '@emotion/css';

import { conSimboloEuro } from '#components/mobile/ui/Importe';
import { color, movimiento, radio, sombra } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';

/**
 * Piezas comunes de los pasos 2–8 de la «Primera travesía»: la hoja que sube
 * sobre la pantalla real (sin tapar la barra de pestañas), las marcas ✓, el
 * interruptor, los chips y el formato de euros. Copia de las clases `.hoja`,
 * `.opcion`, `.marca`, `.interruptor` y `.chip` de la maqueta aprobada.
 */

/** Capas: velo < hoja/Gavi < barra (100) < panel de Inicio < cabecera. */
export const Z_TRAVESIA = {
  velo: 90,
  hoja: 95,
  flota: 95,
  /** Por encima del velo de la brújula (99) y por debajo de sus píldoras (101). */
  inicio: 100,
  cabecera: 110,
} as const;

/** Alto de la cabecera «Paso N de 8» (sin la zona segura). */
export const ALTO_CABECERA = 40;

/** Encima de la barra de pestañas (62 + 10 de margen + 6 de aire). */
export const ENCIMA_BARRA = 'calc(78px + env(safe-area-inset-bottom, 0px))';

const sube = keyframes({
  from: { transform: 'translateY(110%)' },
  to: { transform: 'none' },
});
const aparece = keyframes({ from: { opacity: 0 } });
const nace = keyframes({
  from: { opacity: 0, transform: 'translateY(-6px) scale(.98)' },
});

/** Entrada de algo recién creado (la maqueta: `.nueva`). */
export const claseNace = css({
  animation: `${nace} .45s ${movimiento.muelle}`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const claseHoja = css({
  animation: `${sube} ${movimiento.hoja}ms ${movimiento.muelle}`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  '& .cuerpo': { scrollbarWidth: 'none' },
  '& .cuerpo::-webkit-scrollbar': { display: 'none' },
});

type HojaTravesiaProps = {
  'aria-label': string;
  'data-testid'?: string;
  /** Fijo arriba: Gavi y su bocadillo. */
  cabeza: ReactNode;
  /** Con scroll: la pregunta y sus opciones. */
  children: ReactNode;
  /** Fijo abajo, con filo: el botón del paso. */
  pie: ReactNode;
  /** Se va hacia abajo (al terminar el paso). */
  bajando?: boolean;
};

/**
 * Hoja de un paso: sube sobre la pantalla real y se queda encima de la barra
 * de pestañas (que sigue a la vista con la pestaña del paso encendida). No
 * se cierra tocando el velo: el paso solo avanza con su botón o «Saltar».
 */
export function HojaTravesia({
  'aria-label': ariaLabel,
  'data-testid': testId,
  cabeza,
  children,
  pie,
  bajando = false,
}: HojaTravesiaProps) {
  return createPortal(
    <>
      {!bajando && (
        <div
          aria-hidden
          data-testid="velo-travesia"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: Z_TRAVESIA.velo,
            backgroundColor: color.veloHoja,
            animation: `${aparece} ${movimiento.fondo}ms ease-out`,
          }}
        />
      )}
      <section
        role="dialog"
        aria-modal
        aria-label={ariaLabel}
        data-testid={testId}
        className={claseHoja}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: ENCIMA_BARRA,
          maxHeight: `calc(100% - ${ALTO_CABECERA + 160}px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))`,
          zIndex: Z_TRAVESIA.hoja,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: color.surfaceHoja,
          borderTop: color.bordeHoja,
          borderRadius: radio.heroe,
          boxShadow: sombra.hoja,
          color: color.fg,
          transition: `transform 300ms ease-in`,
          transform: bajando ? 'translateY(130%)' : undefined,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40,
            height: 5,
            borderRadius: 9,
            backgroundColor: color.line2,
            margin: '8px auto 0',
            flex: 'none',
          }}
        />
        <div style={{ padding: '0 16px 4px', flex: 'none' }}>{cabeza}</div>
        <div
          className="cuerpo"
          style={{
            overflowY: 'auto',
            padding: '4px 16px 8px',
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </div>
        <div
          style={{
            padding: '8px 16px 12px',
            flex: 'none',
            borderTop: `1px solid ${color.line}`,
          }}
        >
          {pie}
        </div>
      </section>
    </>,
    document.body,
  );
}

/** Pregunta de la hoja (19 px, 800). */
export function TituloHoja({ children }: { children: ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 19,
        fontWeight: 800,
        margin: '4px 2px 12px',
        lineHeight: 1.25,
        color: color.fg,
      }}
    >
      {children}
    </h2>
  );
}

/** Etiqueta de un campo o grupo de chips. */
export function Etiqueta({
  children,
  htmlFor,
}: {
  children: ReactNode;
  htmlFor?: string;
}) {
  const estilo: CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: color.fg2,
    margin: '0 2px 6px',
    display: 'block',
  };
  return htmlFor ? (
    <label htmlFor={htmlFor} style={estilo}>
      {children}
    </label>
  ) : (
    <span style={estilo}>{children}</span>
  );
}

/** Estilo de los campos de texto de las hojas (`.campo`). */
export const estiloCampo: CSSProperties = {
  width: '100%',
  minHeight: 50,
  borderRadius: 14,
  backgroundColor: color.surface2,
  border: `1px solid ${color.line}`,
  padding: '0 14px',
  fontSize: 16,
  color: color.fg,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  outline: 'none',
};

/** Lista de opciones sobre surface-2 (`.opciones`). */
export const estiloOpciones: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: color.surface2,
  borderRadius: 16,
  overflow: 'hidden',
};

/** Fila de opción (`.opcion`): 56 px, icono, texto y marca. */
export function estiloOpcion(primera: boolean): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 56,
    padding: '8px 12px',
    width: '100%',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 15,
    color: color.fg,
    borderRadius: 0,
    justifyContent: 'flex-start',
    boxShadow: primera ? undefined : `inset 0 1px 0 ${color.line}`,
    background: 'none',
    border: 0,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxSizing: 'border-box',
    WebkitTapHighlightColor: 'transparent',
  };
}

/** Círculo con ✓ (marcado en acento). */
export function Marca({
  marcada,
  size = 24,
}: {
  marcada: boolean;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      data-marcada={marcada || undefined}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `2px solid ${marcada ? color.accent : color.line2}`,
        backgroundColor: marcada ? color.accent : 'transparent',
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        color: color.accentInk,
        fontSize: 14,
        fontWeight: 800,
        boxSizing: 'border-box',
        transition: 'background-color .18s, border-color .18s',
      }}
    >
      {marcada ? '✓' : ''}
    </span>
  );
}

/** Interruptor de 48 × 28 (`.interruptor`). */
export function Interruptor({ encendido }: { encendido: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 48,
        height: 28,
        borderRadius: 99,
        backgroundColor: encendido ? color.accent : color.surface3,
        position: 'relative',
        flex: 'none',
        transition: 'background-color .18s',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: 3,
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
          transform: encendido ? 'translateX(20px)' : 'none',
          transition: `transform .18s ${movimiento.muelle}`,
        }}
      />
    </span>
  );
}

/** Chip de elección (`.chip`): 40 px, borde de acento al elegirlo. */
export function Chip({
  elegido,
  onPress,
  children,
  'data-testid': testId,
}: {
  elegido: boolean;
  onPress: () => void;
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <Button
      variant="bare"
      aria-pressed={elegido}
      data-testid={testId}
      onPress={onPress}
      style={{
        minHeight: 40,
        padding: '6px 14px',
        borderRadius: 999,
        backgroundColor: elegido ? color.accentSoft : color.surface2,
        color: elegido ? color.accent : color.fg2,
        border: `1.5px solid ${elegido ? color.accent : 'transparent'}`,
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {children}
    </Button>
  );
}

/**
 * Euros con el formato del presupuesto: `eur(65000)` → «650,00 €»;
 * `eur0(65000)` → «650 €» (sin céntimos si son cero).
 */
export function useEuros() {
  const format = useFormat();
  const eur = (centimos: number) =>
    conSimboloEuro(format(centimos, 'financial'));
  const eur0 = (centimos: number) => sinCentimosCero(eur(centimos));
  return { eur, eur0 };
}

/** Quita «,00» (o «.00») de un importe ya formateado. */
export function sinCentimosCero(cifra: string): string {
  return cifra.replace(/[.,]00(?=\D*$)/, '');
}

/**
 * Lo escrito en un campo de saldo → céntimos («1.234,5» → 123450). Vacío o
 * ilegible → `null`.
 */
export function leerImporte(texto: string): number | null {
  const limpio = texto.replace(/[\s€ ]/g, '');
  if (!limpio) {
    return null;
  }
  // «1.234,56» (es-ES) o «1234.56»: la coma manda si la hay.
  // Sin coma, «1.500» son mil quinientos (puntos de miles).
  const normal = limpio.includes(',')
    ? limpio.replace(/\./g, '').replace(',', '.')
    : /^-?\d{1,3}(\.\d{3})+$/.test(limpio)
      ? limpio.replace(/\./g, '')
      : limpio;
  const n = Number(normal);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** El teclado de asignar está abierto (`body[data-teclado-abierto]`). */
export function useTecladoAbierto(): boolean {
  const [abierto, setAbierto] = useState(
    () => document.body.dataset.tecladoAbierto != null,
  );
  useEffect(() => {
    const mirar = () =>
      setAbierto(document.body.dataset.tecladoAbierto != null);
    const obs = new MutationObserver(mirar);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-teclado-abierto'],
    });
    mirar();
    return () => obs.disconnect();
  }, []);
  return abierto;
}
